import { dominantBrandColour } from '../utils/colourUtils';

// Logo processing runs entirely in the browser — nothing leaves the device and
// there's no AWS/AI dependency. Background removal is a deterministic flood-fill
// of the uniform background; trim/centre + brand-colour extraction are also
// plain canvas work.

/**
 * Remove a uniform/solid background from a logo, in the browser, with no AI.
 * Flood-fills from the image edges, turning pixels that match the background
 * colour (within `tolerance`) transparent. Because it only spreads from the
 * edges, white/colour *inside* the logo is preserved. Best for logos on a plain
 * background; complex photo backgrounds won't come out as cleanly.
 */
export async function removeBackgroundHeuristic(file: File | Blob, tolerance = 36): Promise<Blob> {
    const url = URL.createObjectURL(file);
    try {
        const img = await loadImage(url);
        const maxDim = 1024;
        const scale = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return file;
        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        // Reference background colour from the OPAQUE corner pixels only. If the
        // corners are already transparent, the logo already has a clean background
        // → return it untouched. (This was the bug that washed out logos with a
        // transparent background: transparent corners read as RGB (0,0,0) = black,
        // so the flood-fill treated dark artwork as "background" and erased it.)
        const cornerIdx = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + (w - 1)) * 4];
        let br = 0, bg = 0, bb = 0, opaque = 0;
        for (const c of cornerIdx) {
            if (data[c + 3] >= 128) { br += data[c]; bg += data[c + 1]; bb += data[c + 2]; opaque++; }
        }
        if (opaque === 0) return file; // already transparent background — nothing to remove
        br /= opaque; bg /= opaque; bb /= opaque;

        const tol2 = tolerance * tolerance;
        const visited = new Uint8Array(w * h);
        const stack: number[] = [];
        const consider = (x: number, y: number) => {
            if (x < 0 || y < 0 || x >= w || y >= h) return;
            const p = y * w + x;
            if (visited[p]) return;
            const i = p * 4;
            if (data[i + 3] < 128) return; // already transparent — don't seed or spread through it
            const dr = data[i] - br, dg = data[i + 1] - bg, db = data[i + 2] - bb;
            if (dr * dr + dg * dg + db * db <= tol2) { visited[p] = 1; stack.push(p); }
        };
        for (let x = 0; x < w; x++) { consider(x, 0); consider(x, h - 1); }
        for (let y = 0; y < h; y++) { consider(0, y); consider(w - 1, y); }
        while (stack.length) {
            const p = stack.pop() as number;
            data[p * 4 + 3] = 0; // make transparent
            const x = p % w, y = (p / w) | 0;
            consider(x + 1, y); consider(x - 1, y); consider(x, y + 1); consider(x, y - 1);
        }

        // Phase 2 — clear SMALL enclosed pockets of the background colour that the
        // edge fill couldn't reach: the holes inside letters (counters of D, e, o,
        // P, R…). Large enclosed fills (intentional white areas of the logo) are
        // kept, so only background-sized gaps are removed.
        const maxPocket = Math.max(64, Math.floor(w * h * 0.02));
        for (let s = 0; s < w * h; s++) {
            if (visited[s]) continue;
            const si = s * 4;
            if (data[si + 3] < 128) continue;                          // already transparent
            const sdr = data[si] - br, sdg = data[si + 1] - bg, sdb = data[si + 2] - bb;
            if (sdr * sdr + sdg * sdg + sdb * sdb > tol2) continue;     // not background-coloured
            const comp: number[] = [s];
            visited[s] = 1;
            for (let head = 0; head < comp.length; head++) {
                const p = comp[head];
                const px = p % w, py = (p / w) | 0;
                const tryN = (nx: number, ny: number) => {
                    if (nx < 0 || ny < 0 || nx >= w || ny >= h) return;
                    const np = ny * w + nx;
                    if (visited[np]) return;
                    const j = np * 4;
                    if (data[j + 3] < 128) return;
                    const dr2 = data[j] - br, dg2 = data[j + 1] - bg, db2 = data[j + 2] - bb;
                    if (dr2 * dr2 + dg2 * dg2 + db2 * db2 <= tol2) { visited[np] = 1; comp.push(np); }
                };
                tryN(px + 1, py); tryN(px - 1, py); tryN(px, py + 1); tryN(px, py - 1);
            }
            if (comp.length <= maxPocket) {
                for (const p of comp) data[p * 4 + 3] = 0;
            }
        }

        ctx.putImageData(imageData, 0, 0);
        return await new Promise<Blob>((resolve) => canvas.toBlob((b) => resolve(b || file), 'image/png'));
    } catch {
        return file;
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * Trim the transparent margins around a logo and re-centre it on a tight canvas
 * with a little uniform padding — so the logo sits nicely centred (no lopsided
 * whitespace) wherever it's shown. Aspect ratio is preserved. Falls back to the
 * input blob if the image is fully transparent or can't be read.
 */
export async function trimAndCentre(blob: Blob, paddingRatio = 0.06): Promise<Blob> {
    const url = URL.createObjectURL(blob);
    try {
        const img = await loadImage(url);
        const w = img.naturalWidth, h = img.naturalHeight;
        const src = document.createElement('canvas');
        src.width = w; src.height = h;
        const sctx = src.getContext('2d', { willReadFrequently: true });
        if (!sctx) return blob;
        sctx.drawImage(img, 0, 0);
        const { data } = sctx.getImageData(0, 0, w, h);

        let minX = w, minY = h, maxX = -1, maxY = -1;
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                if (data[(y * w + x) * 4 + 3] > 10) {
                    if (x < minX) minX = x;
                    if (x > maxX) maxX = x;
                    if (y < minY) minY = y;
                    if (y > maxY) maxY = y;
                }
            }
        }
        if (maxX < minX || maxY < minY) return blob; // nothing opaque — leave as-is

        const bw = maxX - minX + 1, bh = maxY - minY + 1;
        const pad = Math.round(Math.max(bw, bh) * paddingRatio);
        const out = document.createElement('canvas');
        out.width = bw + pad * 2;
        out.height = bh + pad * 2;
        const octx = out.getContext('2d');
        if (!octx) return blob;
        octx.drawImage(src, minX, minY, bw, bh, pad, pad, bw, bh);
        return await new Promise<Blob>((resolve) => out.toBlob((b) => resolve(b || blob), 'image/png'));
    } catch {
        return blob;
    } finally {
        URL.revokeObjectURL(url);
    }
}

function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // needed for getImageData on remote (S3) URLs
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

/**
 * Sample the dominant brand colour from a logo (Blob or URL) on a small
 * offscreen canvas. Returns a hex colour, or null for an essentially
 * greyscale logo (caller can then keep the default / ask the admin to pick).
 */
export async function extractBrandColour(source: Blob | string): Promise<string | null> {
    const url = typeof source === 'string' ? source : URL.createObjectURL(source);
    try {
        const img = await loadImage(url);
        const target = 64;
        const scale = Math.min(target / img.naturalWidth, target / img.naturalHeight, 1);
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        return dominantBrandColour(data);
    } catch {
        return null;
    } finally {
        if (typeof source !== 'string') URL.revokeObjectURL(url);
    }
}
